import { Mixer } from "./Mixer.js";
import { Dispatcher } from "./Dispatcher.js";
import { OutputConfig } from "./OutputConfig.js";
import { audio_suspended, has_joined, needs_user_resume, video_suspended } from "./stores.js";

/*
 Media lifecycle: two INPUTS, two derived OUTPUTS, one place that applies them.

   inputs                                outputs
   ------                                -------
   page hidden        --.
                        >-- video_suspended = mobile && (hidden || audio_off)
   audioContext ok  --'--- audio_suspended  = joined && !running

 The table:

   audio interrupted, page visible   (call banner, Siri, PiP, a tiled app taking the
                                      audio session)              -> video + audio suspended
   audio interrupted, page hidden    (backgrounded onto a call)   -> video + audio suspended
   page hidden only                  (backgrounded, device locked)-> video suspended

 Everything is DERIVED from the current inputs on every event and diffed against what was last
 applied, so no path can leave a latched output behind, and an input that arrives before
 OutputConfig exists simply recomputes correctly on the next one.

 `audio_suspended` is an OBSERVATION (the OS took the audio session; we never suspend audio
 ourselves) -- what makes it worth a store is its FALLING edge: iOS pauses every media element
 during an interruption and only un-pauses them when it supplies the ShouldResume hint, so the
 tiles need a signal to re-play theirs. `video_suspended` is an ACTION, and only worth taking on
 mobile (iOS + Android): on desktop a hidden tab's RAFs are already throttled to zero by the
 browser, and suspending would cost a decoder reset + keyframe resync for nothing. The overlay
 and the relay kick stay iOS-only: they answer iOS audio-session semantics, not backgrounding.
*/

export const MediaLifecycle = {

    // -- INPUTS
    _hidden: false,
    _audio_ok: true,

    // -- OUTPUTS as last applied (edge detection)
    _video_off: false,
    _audio_off: false,

    _was_interrupted: false,

    init(){
        Mixer.onStateChange = (state) => this._setAudioOk(state === "running");
        document.addEventListener("visibilitychange", () => this._setHidden(document.visibilityState === "hidden"));
        this._hidden = document.visibilityState === "hidden";
    },

    _isIOS(){
        return OutputConfig.session?.browser?.isIOS === true;
    },

    _isMobile(){
        const b = OutputConfig.session?.browser;
        return b?.isIOS === true || b?.isAndroid === true;
    },

    _setHidden(hidden){

        if (hidden === this._hidden)
            return;

        this._hidden = hidden;
        Dispatcher.setPageHidden(hidden);
        this._apply();

        // -- returning to a visible page is a chance to get audio back without a gesture. If iOS
        // -- refuses, _recoverAudio's own _setAudioOk keeps the overlay up and the tap does it.
        if (!hidden)
            this._recoverAudio();
    },

    _setAudioOk(ok){

        if (ok === this._audio_ok)
            return;

        this._audio_ok = ok;
        this._apply();
    },

    _apply(){

        const ios = this._isIOS();
        const mobile = this._isMobile();

        // -- pre-join "suspended" is the normal not-yet-gestured state on every platform, so the
        // -- audio session only counts as lost once we are actually in session
        const audio_off = _has_joined && !this._audio_ok;
        const video_off = mobile && (this._hidden || audio_off);

        if (video_off !== this._video_off){
            this._video_off = video_off;

            Dispatcher.setVideoSuspended(video_off);   // -- worker: decoder RAF + video decode

            video_suspended.set(video_off);            // -- main thread: tile pumps + level meters
            console.log("[PL-Lifecycle] Video", video_off ? "suspended" : "resumed");
        }

        if (audio_off !== this._audio_off){

            this._audio_off = audio_off;
            audio_suspended.set(audio_off);

            // -- derived, not latched: an interruption that ends on its own (iOS does resume the
            // -- context for some endings) drops the overlay without a tap
            needs_user_resume.set(ios && audio_off);

            // -- iOS only: an interruption also tends to leave both sockets as OPEN-reporting
            // -- zombies, so the recovery kicks them. A desktop context suspend (output device
            // -- loss) doesn't touch the network and must not disrupt it.
            if (ios && audio_off)
                this._was_interrupted = true;

            console.log("[PL-Lifecycle] Audio session", audio_off ? "lost" : "back");
        }
    },

    async _recoverAudio(){

        const ok = await Mixer.resume();

        // -- onstatechange has usually beaten us here, in which case this is a no-op
        this._setAudioOk(ok);

        if (ok && this._was_interrupted){
            this._was_interrupted = false;
            Dispatcher.reconnectRelays();
        }
    },

    // -- called from the tap-to-resume gesture. Nothing else to do: the resulting state change
    // -- drops the overlay, un-suspends video and re-plays the tiles' elements through _apply.
    userResume(){
        this._recoverAudio();
    },
};

let _has_joined = false;
has_joined.subscribe(value => {
    _has_joined = value;
    MediaLifecycle._apply();   // -- joining while the session is interrupted must still register
});
